import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import React, { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppearanceProvider, useAppearance } from './context/AppearanceContext';
import type { FontSizeMode } from './context/AppearanceContext';
import { MarketDataProvider } from './context/MarketDataContext';
import { NotificationLogProvider } from './context/NotificationLogContext';
import { OrderPanelProvider } from './context/OrderPanelContext';
import { WebSocketProvider } from './context/WebSocketContext';

// Lazy load heavy components for code splitting
const TradingDashboard = lazy(() => import('./components/TradingDashboard'));
const Login = lazy(() => import('./components/Login'));
const PerformanceHistory = lazy(() => import('./pages/PerformanceHistory'));

const getAppTheme = (mode: 'dark' | 'light', fontSizeMode: FontSizeMode) => {
  const baseFontSize = fontSizeMode === 'small' ? 11 : fontSizeMode === 'large' ? 13 : 12;

  return createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#00c853' : '#0b8043',
    },
    secondary: {
      main: mode === 'dark' ? '#f44336' : '#c62828',
    },
    background: {
      default: mode === 'dark' ? '#121212' : '#f5f7fa',
      paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
    },
    success: {
      main: mode === 'dark' ? '#00c853' : '#2e7d32',
    },
    error: {
      main: mode === 'dark' ? '#f44336' : '#d32f2f',
    },
    warning: {
      main: mode === 'dark' ? '#ff9800' : '#ed6c02',
    },
    info: {
      main: mode === 'dark' ? '#2196f3' : '#0288d1',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: baseFontSize,
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: mode === 'dark' ? 'rgba(245, 245, 245, 0.92)' : 'rgba(15, 23, 42, 0.92)',
          backgroundColor: mode === 'dark' ? '#121212' : '#f5f7fa',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          margin: '0 !important',
          padding: '0 !important',
          width: '100vw !important',
          maxWidth: '100vw !important',
          left: '0 !important',
          right: '0 !important',
          position: 'static',
          boxSizing: 'border-box',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          margin: '0 !important',
          paddingLeft: '16px !important',
          paddingRight: '0 !important',
          width: '100% !important',
          maxWidth: '100% !important',
          minHeight: '64px !important',
        },
      },
    },
  },
  });
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        backgroundColor: isLight ? '#f5f7fa' : '#121212',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
      <Box
        sx={{
          color: isLight ? 'rgba(15, 23, 42, 0.9)' : '#00c853',
          fontSize: '1.2rem',
          fontWeight: 500,
        }}
      >
        Loading Trading Platform...
      </Box>
    </Box>
  );
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLoginSuccess={() => { }} />
            )
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <TradingDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance-history"
          element={
            <ProtectedRoute>
              <PerformanceHistory />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

const AppContent: React.FC = () => {
  const { appearance } = useAppearance();
  const appTheme = useMemo(
    () => getAppTheme(appearance.themeMode, appearance.fontSize),
    [appearance.themeMode, appearance.fontSize]
  );

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NotificationLogProvider>
            <WebSocketProvider>
              <MarketDataProvider>
                <OrderPanelProvider>
                  <div
                    className="App"
                    style={{
                      margin: 0,
                      padding: 0,
                      width: '100%',
                      height: '100vh',
                      boxSizing: 'border-box',
                    }}
                  >
                    <AppRoutes />
                  </div>
                </OrderPanelProvider>
              </MarketDataProvider>
            </WebSocketProvider>
          </NotificationLogProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

function App() {
  return (
    <AppearanceProvider>
      <AppContent />
    </AppearanceProvider>
  );
}

export default App;